import type { NormalizedDataSource, PlatformKey, DataSourceEntry } from './datasource.types';
import { SupportedTools } from '../../../interfaces';
import { datasourceMap } from './datasource.registry';

const uniq = (a?: string[]) => (a ? Array.from(new Set(a)) : a);

// Map common sigma synonyms
function normalizePlatform(p: string): PlatformKey | undefined {
  const v = p.toLowerCase().trim();
  if (v === 'windows' || v === 'win') return 'windows';
  if (v === 'linux' || v === 'lin') return 'linux';
  if (v === 'mac' || v === 'macos' || v === 'osx') return 'mac';
  if (v === 'any' || v === '*') return 'any';
  if (v === 'azure') return 'azure';
  if (v === 'aws') return 'aws';
  if (v === 'gcp') return 'gcp';
  if (v === 'cloud') return 'cloud';
  // set undefined if unknown
  return undefined;
}

export function resolveDatasource(
  tool: SupportedTools,
  platforms: string[] | undefined,
  categories: string[],
): NormalizedDataSource | undefined {
  // Normalize categories
  const normCats =
    uniq((categories ?? []).map((c) => String(c).toLowerCase().trim()).filter(Boolean)) ?? [];
  if (normCats.length === 0) return undefined;

  // Normalize platforms
  const normPlatformKeys: PlatformKey[] = (() => {
    const keys = (platforms ?? [])
      .map(normalizePlatform)
      .filter((x): x is PlatformKey => Boolean(x));
    return keys.length ? Array.from(new Set(keys)) : (['any'] as PlatformKey[]);
  })();

  // Collect available maps for tool/platforms, always including 'any' as a fallback
  const platformsToCheck: PlatformKey[] = normPlatformKeys.includes('any')
    ? normPlatformKeys
    : [...normPlatformKeys, 'any'];
  const maps = platformsToCheck.map((pk) => datasourceMap[tool]?.[pk]).filter(Boolean);
  if (!maps.length) return undefined;

  // Create merged payload; categories come from input AST, not map entries
  const merged: NormalizedDataSource = {
    categories: normCats,
    platform: platforms && platforms.length ? platforms : undefined,
    events: [],
    eventCodes: [],
    keywords: [],
    extra: {},
  };

  // Collect routing fields across all matched platform entries into sets so multi-cloud
  // entries (aws + azure + gcp) can each contribute their own repo/repoSubtype/eventProvider.
  const reposSet = new Set<string>();
  const repoSubtypeSet = new Set<string>();
  const eventProvSet = new Set<string>();

  // Merge each category across all selected platform maps
  for (const cat of normCats) {
    for (const toolMap of maps) {
      const ds: DataSourceEntry | undefined = toolMap![cat];
      if (!ds) continue;

      if (ds.events?.length) merged.events!.push(...ds.events);
      if (ds.eventCodes?.length) merged.eventCodes!.push(...ds.eventCodes);
      if (ds.keywords?.length) merged.keywords!.push(...ds.keywords);
      if (ds.selectFields?.length)
        merged.selectFields = [...(merged.selectFields ?? []), ...ds.selectFields];
      if (ds.repo) reposSet.add(ds.repo);
      if (ds.repoSubtype) repoSubtypeSet.add(ds.repoSubtype);
      if (ds.eventProvider) eventProvSet.add(ds.eventProvider);

      if (ds.extra) {
        const nextExtra = { ...(merged.extra ?? {}), ...ds.extra };
        // Back-compat: promote single platformClause → list
        const single = (ds.extra as any)['platformClause'] as string | undefined;
        if (single) {
          nextExtra.platformClauseList = [...(nextExtra.platformClauseList ?? []), single];
          delete (nextExtra as any)['platformClause'];
        }
        const list = (ds.extra as any)['platformClauseList'] as string[] | undefined;
        if (list?.length) {
          nextExtra.platformClauseList = [...(nextExtra.platformClauseList ?? []), ...list];
        }
        merged.extra = nextExtra;
      }
    }
  }

  // De-dup merged arrays
  merged.events = uniq(merged.events);
  merged.eventCodes = uniq(merged.eventCodes);
  merged.keywords = uniq(merged.keywords);
  if (merged.selectFields?.length) merged.selectFields = uniq(merged.selectFields);
  if (merged.extra?.platformClauseList) {
    merged.extra.platformClauseList = uniq(merged.extra.platformClauseList);
  }

  // Assign routing field arrays; always set singular (first value) for backward compat
  if (reposSet.size) {
    merged.repos = [...reposSet];
    merged.repo = [...reposSet][0];
  }
  if (repoSubtypeSet.size) {
    merged.repoSubtypes = [...repoSubtypeSet];
    merged.repoSubtype = [...repoSubtypeSet][0];
  }
  if (eventProvSet.size) {
    merged.eventProviders = [...eventProvSet];
    merged.eventProvider = [...eventProvSet][0];
  }

  // If everything merged is empty (no events/codes/keywords and no extras), allow returning a minimal object
  // The renderer can decide to skip emitting if its not useful
  return merged;
}
